// ============================================================================
// BOQ — parse a sample BOQ spreadsheet for rate harvesting
// ============================================================================
// External sample BOQs won't use our library codes, so this parser is
// deliberately lenient: it pulls raw rows (description + rate + optional code /
// unit / vendor) and leaves item matching to the import wizard. Required: a
// Description column and at least one rate column.
// ============================================================================

import ExcelJS from 'exceljs';

const numFrom = (cell) => {
  if (cell === null || cell === undefined || cell === '') return null;
  const raw = typeof cell === 'object' && cell.result !== undefined ? cell.result : cell;
  const cleaned = String(raw).replace(/[,$€£₹\s]/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
};

const textFrom = (cell) => {
  if (cell === null || cell === undefined) return '';
  const raw = typeof cell === 'object' && cell.result !== undefined ? cell.result
    : (typeof cell === 'object' && cell.text !== undefined ? cell.text : cell);
  return String(raw).trim();
};

export async function parseRatesFromXlsx(file) {
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);
  const ws = wb.worksheets[0];
  if (!ws) return { rows: [], error: 'No worksheet found.' };

  const headerRow = ws.getRow(1);
  const headerMap = {};
  headerRow.eachCell((cell, col) => {
    const label = String(cell.value || '').trim().toLowerCase();
    if (label) headerMap[label] = col;
  });
  const colFor = (...names) => {
    for (const n of names) { if (headerMap[n.toLowerCase()]) return headerMap[n.toLowerCase()]; }
    return null;
  };

  const descCol = colFor('description', 'item', 'particulars', 'desc');
  const codeCol = colFor('code', 'ref', 'item code');
  const unitCol = colFor('unit', 'uom');
  const quotedCol = colFor('rate', 'rate estimate', 'rate quoted', 'estimate', 'amount');
  const billedCol = colFor('rate tender', 'rate billed', 'tender', 'billed');
  const vendorCol = colFor('vendor', 'supplier', 'subcontractor', 'contractor');

  if (!descCol || (!quotedCol && !billedCol)) {
    return { rows: [], error: 'Sheet must have a Description column and at least one Rate column.' };
  }

  const rows = [];
  const lastRow = ws.rowCount;
  for (let r = 2; r <= lastRow; r++) {
    const row = ws.getRow(r);
    if (!row.hasValues) continue;
    const description = textFrom(row.getCell(descCol).value);
    const rateQuoted = quotedCol ? numFrom(row.getCell(quotedCol).value) : null;
    const rateBilled = billedCol ? numFrom(row.getCell(billedCol).value) : null;
    if (!description && rateQuoted == null && rateBilled == null) continue;
    rows.push({
      rowNumber: r,
      code: codeCol ? textFrom(row.getCell(codeCol).value) : '',
      description,
      unit: unitCol ? textFrom(row.getCell(unitCol).value) : '',
      rateQuoted,
      rateBilled,
      vendor: vendorCol ? textFrom(row.getCell(vendorCol).value) : '',
    });
  }
  return { rows, error: null };
}
